class CoastalDiscovery extends HTMLElement {
  connectedCallback() {
    if (this.events) return;
    this.tabs = [...this.querySelectorAll('[data-collection-id][href]')];
    this.panel = this.querySelector('.sc-discovery__panel');
    this.status = this.querySelector('[role="status"]');
    const list = this.querySelector('nav');
    if (!this.tabs.length || !this.panel || !this.status || !list) return;
    this.events = new AbortController();
    list.setAttribute('role', 'tablist');
    this.tabs.forEach(tab => {
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', this.panel.id);
      tab.addEventListener('click', event => {
        if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.select(tab);
      }, { signal: this.events.signal });
      tab.addEventListener('keydown', event => {
        const index = this.tabs.indexOf(tab);
        const rtl = getComputedStyle(this).direction === 'rtl';
        let next;
        if (event.key === 'ArrowRight') next = (index + (rtl ? -1 : 1) + this.tabs.length) % this.tabs.length;
        if (event.key === 'ArrowLeft') next = (index + (rtl ? 1 : -1) + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.tabs.length - 1;
        if (next !== undefined) {
          event.preventDefault();
          this.tabs.forEach(item => { item.tabIndex = -1; });
          this.tabs[next].tabIndex = 0;
          this.tabs[next].focus({ preventScroll: true });
          this.tabs[next].scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
        if (event.key === ' ') { event.preventDefault(); this.select(tab); }
      }, { signal: this.events.signal });
    });
    this.activate(this.tabs.find(tab => tab.dataset.collectionId === this.panel.dataset.collectionId) || this.tabs[0]);
  }
  activate(tab) {
    this.activeTab = tab;
    this.tabs.forEach(item => {
      const active = item === tab;
      item.setAttribute('aria-selected', String(active));
      item.tabIndex = active ? 0 : -1;
      item.removeAttribute('aria-current');
    });
    this.panel.setAttribute('role', 'tabpanel');
    this.panel.setAttribute('aria-labelledby', tab.id);
    this.panel.tabIndex = 0;
  }
  async select(tab) {
    this.request?.abort();
    if (tab === this.activeTab) {
      this.panel.removeAttribute('aria-busy');
      this.status.textContent = '';
      return;
    }
    const request = new AbortController();
    this.request = request;
    const timeout = setTimeout(() => request.abort(new DOMException('Collection request timed out', 'TimeoutError')), 15000);
    this.panel.setAttribute('aria-busy', 'true');
    this.status.removeAttribute('data-success');
    this.status.textContent = this.dataset.loading;
    try {
      const url = new URL(tab.href, location.href);
      if (url.origin !== location.origin || !this.dataset.sectionId) throw new Error('Invalid collection route');
      url.searchParams.set('section_id', this.dataset.sectionId);
      const response = await fetch(url, { signal: request.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Collection returned HTTP ${response.status}`);
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const replacement = document.querySelector('sc-coastal-discovery .sc-discovery__panel');
      if (!replacement || replacement.dataset.collectionId !== tab.dataset.collectionId) throw new Error('Collection response did not match the selected tab');
      if (this.request !== request || request.signal.aborted) return;
      const browse = replacement.querySelector('.sc-discovery__browse');
      if (browse) {
        browse.href = tab.href;
        const arrow = browse.querySelector('.sc-arrow');
        browse.textContent = tab.textContent.trim();
        if (arrow) browse.append(arrow);
      }
      this.panel.replaceWith(replacement);
      this.panel = replacement;
      this.activate(tab);
      this.status.setAttribute('data-success', '');
      this.status.textContent = `${tab.textContent.trim()}. ${this.dataset.loaded}`;
      if (!matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.panel.animate([{ opacity: .65 }, { opacity: 1 }], { duration: 220, easing: 'ease-out' });
      }
    } catch (error) {
      if (this.request !== request || error.name === 'AbortError') return;
      this.status.removeAttribute('data-success');
      this.status.textContent = this.dataset.error;
      const fallback = document.createElement('a');
      fallback.href = tab.href;
      fallback.textContent = ` ${tab.textContent.trim()} `;
      const arrow = this.querySelector('.sc-arrow');
      if (arrow) fallback.append(arrow.cloneNode(true));
      this.status.append(fallback);
    } finally {
      clearTimeout(timeout);
      if (this.request === request) this.panel.removeAttribute('aria-busy');
    }
  }
  disconnectedCallback() {
    this.request?.abort();
    this.events?.abort();
    this.events = null;
  }
}
if (!customElements.get('sc-coastal-discovery')) customElements.define('sc-coastal-discovery', CoastalDiscovery);
